import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  useTheme,
} from '@mui/material';
import { useAuth } from './useAuth';

/**
 * Login page — styled to match the GWT login form.
 * GWT uses a basic centered form with the logo above.
 */
export const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      await login(username, password);
      navigate('/');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      sx={{ backgroundColor: 'background.default' }}
    >
      <Paper
        elevation={2}
        sx={{
          p: 4,
          width: '100%',
          maxWidth: 400,
          backgroundColor: 'background.paper',
        }}
      >
        <Box textAlign="center" mb={3}>
          <Box sx={{ position: 'relative', display: 'inline-block' }}>
            <Typography
              variant="h5"
              component="h1"
              sx={{ color: 'primary.main', fontWeight: 700 }}
            >
              Roentgen Works
            </Typography>
            <Typography
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%) rotate(-12deg)',
                color: '#e53935',
                fontWeight: 900,
                fontSize: 18,
                letterSpacing: '2px',
                lineHeight: 1,
                textTransform: 'uppercase',
                border: '2.5px solid #e53935',
                borderRadius: '4px',
                px: 1.2,
                py: 0.3,
                pointerEvents: 'none',
                userSelect: 'none',
                whiteSpace: 'nowrap',
                fontFamily: '"Impact", "Arial Narrow", sans-serif',
                backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
                boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
              }}
            >
              REACT
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Please sign in to continue
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            label="Username"
            fullWidth
            margin="normal"
            size="small"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoFocus
            disabled={isSubmitting}
          />
          <TextField
            label="Password"
            type="password"
            fullWidth
            margin="normal"
            size="small"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isSubmitting}
          />
          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={isSubmitting}
            sx={{ mt: 2 }}
          >
            {isSubmitting ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>

        <Typography
          variant="caption"
          display="block"
          textAlign="center"
          color="text.secondary"
          sx={{ mt: 3 }}
        >
          © 2026 InsiteOne LLC. All rights reserved.
        </Typography>
      </Paper>
    </Box>
  );
};
